import React, { useState, useContext, useRef } from 'react';
import { Plus, Loader, FileText, Upload } from 'lucide-react';
import { PlansContext } from '../../App';
import { authenticatedFetch } from '../../utils/api';
import styles from './AIPresentationGenerator.module.css';
import mammoth from 'mammoth';
import { getDocument } from 'pdfjs-dist/webpack';

const AIPresentationGenerator = ({ onGeneratedContent }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [topic, setTopic] = useState('');
    const [loading, setLoading] = useState(false);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [extractedText, setExtractedText] = useState('');
    const [extractingFile, setExtractingFile] = useState(false);
    const fileInputRef = useRef(null);
    const { handleLimitReached } = useContext(PlansContext);

    const extractFirstImageUrl = (content) => {
        const imgRegex = /!\[.*?\]\((.*?)\)/;
        const match = content.match(imgRegex);
        return match ? match[1] : '/placeholder-image.jpg';
    };

    const extractTextFromPDF = async (file) => {
        try {
            const arrayBuffer = await file.arrayBuffer();
            const pdf = await getDocument({ data: arrayBuffer }).promise;
            let text = '';

            for (let i = 1; i <= pdf.numPages; i++) {
                const page = await pdf.getPage(i);
                const content = await page.getTextContent();
                text += content.items.map(item => item.str).join(' ') + '\n';
            }

            return text;
        } catch (error) {
            console.error('PDF extraction error:', error);
            throw new Error('Failed to extract text from PDF. Please ensure the PDF is not password-protected and contains readable text.');
        }
    };

    const extractTextFromDOCX = async (file) => {
        try {
            const arrayBuffer = await file.arrayBuffer();
            const result = await mammoth.extractRawText({ arrayBuffer });
            return result.value;
        } catch (error) {
            console.error('DOCX extraction error:', error);
            throw new Error('Failed to extract text from DOCX file. Please ensure the file is not corrupted.');
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setExtractingFile(true);
        setUploadedFile(file);

        try {
            let extractedContent = '';

            if (file.type === 'application/pdf') {
                extractedContent = await extractTextFromPDF(file);
            } else if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
                extractedContent = await extractTextFromDOCX(file);
            } else if (file.type === 'text/plain') {
                extractedContent = await file.text();
            } else {
                throw new Error('Unsupported file type. Please upload a PDF, DOCX, or TXT file.');
            }

            if (!extractedContent.trim()) {
                throw new Error('No readable text found in the document. Please ensure the file contains text content.');
            }

            setExtractedText(extractedContent);
            const topicFromFilename = file.name.split('.')[0]
                .replace(/[-_]/g, ' ')
                .replace(/([A-Z])/g, ' $1')
                .trim();
            setTopic(topicFromFilename);
        } catch (error) {
            console.error('Error extracting file content:', error);
            alert(error.message || 'Failed to extract content from the file. Please try another file or enter the topic manually.');
            setUploadedFile(null);
            setExtractedText('');
        } finally {
            setExtractingFile(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const data = await authenticatedFetch('/api/ai-presentations/generate', {
                method: 'POST',
                body: {
                    topic,
                    documentContent: extractedText || null
                }
            });

            const firstImageUrl = extractFirstImageUrl(data.response);
            const newContent = {
                id: 'ai-' + Date.now(),
                title: topic,
                type: 'presentation',
                author: 'AI Assistant',
                date: new Date().toISOString(),
                thumbnail: firstImageUrl,
                content: data.response
            };

            onGeneratedContent(newContent);
            setIsExpanded(false);
            setTopic('');
            setUploadedFile(null);
            setExtractedText('');
        } catch (error) {
            console.error('Failed to generate presentation:', error);
            if (error.limitReached) {
                handleLimitReached();
            }
        } finally {
            setLoading(false);
        }
    };

    if (!isExpanded) {
        return (
            <div className={styles.card} onClick={() => setIsExpanded(true)}>
                <div className={styles.cardInner}>
                    <div className={styles.iconWrapper}>
                        <FileText size={24} className={styles.icon} />
                    </div>
                    <h3 className={styles.cardTitle}>Generate New Presentation</h3>
                    <p className={styles.cardDescription}>
                        Create an AI-powered presentation with custom content
                    </p>
                    <div className={styles.createButton}>
                        <Plus size={20} />
                        <span>Create New</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.card}>
            <div className={styles.formWrapper}>
                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.formHeader}>
                        <h3>Generate New Presentation</h3>
                        <p className={styles.formDescription}>
                            Enter a topic or upload a document to generate a presentation
                        </p>
                    </div>

                    <div className={styles.inputGroup}>
                        <label htmlFor="topic">Presentation Topic</label>
                        <input
                            id="topic"
                            type="text"
                            value={topic}
                            onChange={(e) => setTopic(e.target.value)}
                            placeholder="E.g., Introduction to Machine Learning..."
                            className={styles.input}
                            disabled={loading || extractingFile}
                        />
                    </div>

                    <div className={styles.uploadSection}>
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileUpload}
                            accept=".pdf,.docx,.txt"
                            style={{ display: 'none' }}
                            disabled={loading || extractingFile}
                        />
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className={styles.uploadButton}
                            disabled={loading || extractingFile}
                        >
                            <Upload size={18} />
                            {extractingFile ? 'Extracting...' : 'Upload Document'}
                        </button>
                        {uploadedFile && (
                            <p className={styles.fileName}>
                                {uploadedFile.name}
                            </p>
                        )}
                    </div>

                    <div className={styles.buttonGroup}>
                        <button
                            type="button"
                            onClick={() => setIsExpanded(false)}
                            className={styles.cancelButton}
                            disabled={loading || extractingFile}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={styles.generateButton}
                            disabled={loading || extractingFile || !topic.trim()}
                        >
                            {loading ? (
                                <>
                                    <Loader className={styles.spinner} />
                                    Generating...
                                </>
                            ) : (
                                <>
                                    <FileText size={18} />
                                    Generate
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AIPresentationGenerator;